import { Component, type ReactNode } from 'react';
import { butterToast } from '@butter/ui/patterns';
import { captureException } from '@butter/monitoring/web';
import { classifyError, ERROR_TITLES, type ClassifiedError } from './errorTypes';

interface GlobalErrorHandlerProps {
  children: ReactNode;
}

/**
 * Global Error Handler
 *
 * Catches unhandled promise rejections and global errors that escape
 * React error boundaries. This is essential for catching:
 * - Network errors from fetch/axios
 * - Async errors outside React lifecycle
 * - Third-party library errors
 */
export class GlobalErrorHandler extends Component<GlobalErrorHandlerProps> {
  componentDidMount() {
    // Handle unhandled promise rejections (network errors, async failures)
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);

    // Handle uncaught errors
    window.addEventListener('error', this.handleGlobalError);
  }

  componentWillUnmount() {
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
    window.removeEventListener('error', this.handleGlobalError);
  }

  handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    event.preventDefault(); // Prevent console noise

    this.report(
      'Unhandled rejection',
      classifyError(event.reason),
      event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
    );
  };

  handleGlobalError = (event: ErrorEvent) => {
    event.preventDefault(); // Prevent console noise

    this.report(
      'Global error',
      classifyError(event.error),
      event.error instanceof Error ? event.error : new Error(event.message),
    );
  };

  /** Log in dev, report to monitoring, surface to the user. */
  report(label: string, classified: ClassifiedError, error: Error) {
    if (import.meta.env.DEV) {
      console.error(`[GlobalErrorHandler] ${label}:`, classified);
    }

    captureException(error, { tags: { errorType: classified.type } });

    const message = classified.requiresSupport
      ? `${classified.userMessage} If this issue persists, please contact support.`
      : classified.userMessage;

    // Keyed by classification, not per-occurrence: a failing poll or a burst of
    // rejections from one broken page would otherwise stack a toast per event.
    // Distinct classes still surface separately.
    //
    // 10s, not the Toaster's 5s default: these are uncaught failures with no
    // call-site handler, so this toast is the only signal — and it usually
    // arrives while the user is looking somewhere else. The value carries over
    // from the hand-rolled stack this replaced, which auto-dismissed at 10s.
    butterToast.error(message, ERROR_TITLES[classified.type], {
      id: `global-error-${classified.type}`,
      duration: 10000,
    });
  }

  render() {
    return <>{this.props.children}</>;
  }
}
